import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { deleteHousehold, getHouseholdDeletionStatus, updateHousehold } from "@/api/households";
import type { Household } from "@/types/household";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/design-system/dialogs/ConfirmDialog";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { HouseholdEditor } from "./HouseholdEditor";
import { householdError, refreshHouseholdQueries } from "../management";

export function HouseholdManagement({ household }: { household: Household }) {
  const { permissions } = useEffectivePermissions();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmAssessmentLoss, setConfirmAssessmentLoss] = useState(false);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const deleting = useRef(false);
  const canEdit = permissions.has("HOUSEHOLDS_MANAGE");
  const canDelete = canEdit && permissions.has("SETTINGS_MANAGE");
  const safety = useQuery({
    queryKey: ["household-deletion-status", household.church_id, household.id],
    queryFn: () => getHouseholdDeletionStatus(household.church_id, household.id),
    enabled: canDelete,
  });
  if (!canEdit) return null;
  async function remove() {
    if (deleting.current) return;
    deleting.current = true;
    setPending(true);
    setError("");
    try {
      await deleteHousehold(household.church_id, household.id, confirmAssessmentLoss);
      setConfirming(false);
      navigate("/households", { state: { householdDeleted: true } });
      client.removeQueries({ queryKey: ["household", household.church_id, household.id], exact: true });
      void refreshHouseholdQueries(client, household.church_id);
    } catch (error) {
      setError(householdError(error));
      setConfirming(false);
      void safety.refetch();
    } finally { deleting.current = false; setPending(false); }
  }
  return <div className="mb-5 space-y-3">
    <div className="flex flex-wrap gap-3">
      <Button variant="secondary" onClick={() => { setFeedback(""); setEditing(true); }}><Pencil className="mr-2 inline h-4 w-4" />Edit Household</Button>
      {canDelete && <Button variant="danger" disabled={!safety.data?.can_delete || safety.isError || safety.isFetching || pending}
        aria-describedby="household-delete-explanation" onClick={() => { setError(""); setConfirmAssessmentLoss(safety.data?.will_delete_assessment_history === true); setConfirming(true); }}><Trash2 className="mr-2 inline h-4 w-4" />Delete Household</Button>}
    </div>
    {canDelete && <div id="household-delete-explanation" className="text-sm text-slate-400">
      {safety.isFetching ? "Checking whether this household can be deleted…" : safety.isError ? <span>Unable to check deletion safety. <button className="underline" onClick={() => void safety.refetch()}>Retry</button></span> :
        safety.data?.can_delete ? (safety.data.will_delete_assessment_history ? "This empty household can be deleted, but its household assessments and derived intelligence history will be permanently deleted." : "This household has no members or protected dependencies and can be permanently deleted.") :
          safety.data?.reasons.map(reason => <p key={reason}>{reason}</p>)}
    </div>}
    {feedback && <p role="status" className="text-sm text-emerald-300">{feedback}</p>}
    {error && <p role="alert" className="text-sm text-rose-300">{error}</p>}
    <Modal open={editing} onClose={() => { if (!pending) setEditing(false); }} ariaLabelledBy="edit-household-title" panelClassName="max-w-2xl">
      <h2 id="edit-household-title" className="mb-2 text-xl font-semibold">Edit Household</h2>
      <p className="mb-5 text-sm text-slate-400">Update household details. Members and their history stay connected.</p>
      <HouseholdEditor initial={household} onPendingChange={setPending} onCancel={() => setEditing(false)} onSave={async payload => {
        await updateHousehold(household.church_id, household.id, payload);
        await refreshHouseholdQueries(client, household.church_id);
        setEditing(false);
        setFeedback("Household updated successfully.");
      }} />
    </Modal>
    <ConfirmDialog open={confirming} title="Permanently delete household?" description={confirmAssessmentLoss ? `Delete ${household.household_name}? This household has no members and can be deleted. Warning: MinistryIQ has historical household assessments for this household. Deleting the household will permanently delete those assessments and their derived intelligence history. This cannot be undone. The household's former Members and their individual attendance, care, ministry and other Member history will NOT be deleted.` : `Delete ${household.household_name}? This cannot be undone. The server will check dependencies again before deleting.`}
      destructive confirmText="Permanently Delete" pending={pending} onCancel={() => setConfirming(false)} onConfirm={() => void remove()} />
  </div>;
}
